/**
 * THE SAME NUMBERS AS ROWS.
 *
 * Three jobs, one component:
 *
 *   · the ACCESSIBILITY FALLBACK for the chart beside it — a `<table>` is what a
 *     screen reader can actually read, where an SVG of bars is not,
 *   · the thing a manager copies into an email,
 *   · and the shape the export writes, so the file and the screen cannot
 *     disagree about what a row says.
 *
 * The third is why the conversion to rows happens ONCE, upstream, and both the
 * chart and this render from it.
 */
import React from 'react';
import { useCompareMeaning } from './compare-label';
import {
  DataTable,
  SrOnly,
  TableScroller,
  Td,
  Th,
  TotalRow,
} from './chart.styled';

export interface TableRow {
  key: string;
  label: string;
  value: string;
  compareValue?: string;
  delta?: string;
}

export interface ChartTableProps {
  dimensionLabel: string;
  valueLabel: string;
  rows: TableRow[];
  totalRow?: TableRow;
  showCompare: boolean;
  caption?: string;
  /** What the comparison column holds. Defaults to whatever the screen's
   *  comparison currently MEANS — the earlier period, or the other place. It
   *  used to be the hardcoded word "Previous", which with a second scope chosen
   *  put that word over another building's numbers. */
  compareLabel?: string;
  /** What the difference column holds. Defaults to "Change". */
  deltaLabel?: string;
}

export const ChartTable: React.FC<ChartTableProps> = ({
  dimensionLabel,
  valueLabel,
  rows,
  totalRow,
  showCompare,
  caption,
  compareLabel,
  deltaLabel,
}) => {
  const meaning = useCompareMeaning();
  const other = compareLabel || meaning.label;
  const change = deltaLabel || (meaning.isScope ? 'Difference' : 'Change');

  return (
  <TableScroller>
    <DataTable data-testid="panel-table">
      <caption>
        <SrOnly>{caption || `${valueLabel} by ${dimensionLabel}`}</SrOnly>
      </caption>
      <thead>
        <tr>
          <Th scope="col">{dimensionLabel}</Th>
          <Th scope="col" numeric>
            {valueLabel}
          </Th>
          {showCompare ? (
            <>
              <Th scope="col" numeric>
                {other}
              </Th>
              <Th scope="col" numeric>
                {change}
              </Th>
            </>
          ) : null}
        </tr>
      </thead>
      <tbody>
        {rows.map(row => (
          <tr key={row.key}>
            <Td>{row.label}</Td>
            <Td numeric>{row.value}</Td>
            {showCompare ? (
              <>
                <Td numeric>{row.compareValue ?? '—'}</Td>
                <Td numeric>{row.delta ?? '—'}</Td>
              </>
            ) : null}
          </tr>
        ))}
        {totalRow ? (
          <TotalRow>
            <Td>{totalRow.label}</Td>
            <Td numeric>{totalRow.value}</Td>
            {showCompare ? (
              <>
                <Td numeric>{totalRow.compareValue ?? '—'}</Td>
                <Td numeric>{totalRow.delta ?? '—'}</Td>
              </>
            ) : null}
          </TotalRow>
        ) : null}
      </tbody>
    </DataTable>
  </TableScroller>
  );
};

/**
 * A one-sentence summary of a series, for the chart's `aria-label`.
 *
 * A screen reader handed an SVG of forty bars gets nothing. This is what it gets
 * instead, and it is deliberately the same three facts a sighted reader takes
 * from a glance: where it starts, where it ends, and where the peak is.
 */
export const describeSeries = (
  label: string,
  points: { label: string; value: number }[],
  noun: [string, string] = ['record', 'records']
): string => {
  if (!points.length) return `${label}: no data.`;
  const first = points[0];
  const last = points[points.length - 1];
  const peak = points.reduce((best, p) => (p.value > best.value ? p : best), points[0]);
  const total = points.reduce((sum, p) => sum + p.value, 0);
  const noteOf = (n: number) => `${n.toLocaleString()} ${n === 1 ? noun[0] : noun[1]}`;

  /* ONE BUCKET HAS NO TREND AND NO PEAK. Read out of the generic sentence it
     came back as "…is flat from 91 to 91, peak 91 on Sep 4", which a screen
     reader delivers as three restatements of one number — and which is how the
     summary read on the period this whole one-bucket fix came from. */
  if (points.length === 1) {
    return `${label} on ${first.label}: ${noteOf(first.value)}. A single bucket, so there is no trend to read.`;
  }

  /* A SUBJECT, NOT A BARE VERB. This sentence used to start "is flat from…",
     because the direction word was written to follow a series name that was
     never in front of it. */
  const direction =
    last.value > first.value
      ? 'The series rises'
      : last.value < first.value
        ? 'The series falls'
        : 'The series is flat';

  return (
    `${label}, ${first.label} to ${last.label}. ` +
    `${noteOf(total)} in total. ` +
    `${direction} from ${first.value.toLocaleString()} to ${last.value.toLocaleString()}, ` +
    `peak ${peak.value.toLocaleString()} on ${peak.label}.`
  );
};
